import { useCallback, useEffect, useState } from 'react';

import {
  EstimateApyFarm,
  EstimateApyParams,
  useEstimateApy,
} from '@services/api/vaults/vaults.api';

import useDebounce from '@hooks/common/useDebounce';

import { toBN } from '@utils/formaters.utils';

import { PERCENT_DIVIDER } from '@constants/common/common.constants';
import { MAX_FARM_PERCENT } from '@constants/vaults/vault.constants';

import { EstimatedApy } from '@app-types/vaults/farm.types';

export interface Params {
  vaultFactoryId: number | undefined;
  chainId: number | undefined;
  dummyInvestUsd?: number;
  selectedFarms: EstimateApyFarm[];
  isDisabled: boolean;
  takeProfitTokenPercent: number;
}

const INITIAL_STATE = {
  apy: toBN(0),
  dailyApy: toBN(0),
};

export const useEstimateVaultApy = ({
  vaultFactoryId,
  chainId,
  dummyInvestUsd,
  selectedFarms,
  isDisabled,
  takeProfitTokenPercent,
}: Params) => {
  const [estimatedApy, setEstimatedApy] = useState<EstimatedApy>(INITIAL_STATE);

  const { mutateAsync: estimateApyApi, isPending } = useEstimateApy();

  const debouncedSelectedFarms = useDebounce(selectedFarms, { delay: 1000 });
  const debouncedTakeProfitTokenPercent = useDebounce(takeProfitTokenPercent, {
    delay: 1000,
  });

  const onEstimateApy = useCallback(
    async (farms: EstimateApyFarm[], takeProfitPercent: number) => {
      try {
        const totalPercents = farms.reduce(
          (acc, farm) => acc + farm.percent * 100,
          0,
        );

        if (totalPercents !== MAX_FARM_PERCENT || !vaultFactoryId || !chainId) {
          setEstimatedApy(INITIAL_STATE);
          return;
        }

        const data: EstimateApyParams = {
          farms,
          takeProfitPercent: takeProfitPercent / PERCENT_DIVIDER,
          vaultFactoryId,
          chainId,
          dummyInvestUsd: dummyInvestUsd || 1,
        };

        const res = await estimateApyApi(data);

        if (!res || !farms.length) {
          setEstimatedApy(INITIAL_STATE);
        } else {
          setEstimatedApy({
            apy: toBN(res.apy).times(PERCENT_DIVIDER),
            dailyApy: toBN(res.dailyApy).times(PERCENT_DIVIDER),
          });
        }
      } catch (e) {
        console.log(e, 'onEstimateApy');
        setEstimatedApy(INITIAL_STATE);
      }
    },
    [chainId, dummyInvestUsd, estimateApyApi, vaultFactoryId],
  );

  useEffect(() => {
    if (!isDisabled) {
      (async () => {
        await onEstimateApy(
          debouncedSelectedFarms,
          debouncedTakeProfitTokenPercent,
        );
      })();
    } else {
      setEstimatedApy(INITIAL_STATE);
    }
  }, [debouncedSelectedFarms, debouncedTakeProfitTokenPercent, isDisabled]);

  return {
    estimatedApy,
    isPending,
  };
};
